import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

const NotFound: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        // Staggered fade-in for children
        const items = el.querySelectorAll<HTMLElement>('.reveal')
        items.forEach((item, i) => {
            item.style.opacity = '0'
            item.style.transform = 'translateY(24px)'
            item.style.transition = `opacity 0.7s ease ${i * 0.12}s, transform 0.7s ease ${i * 0.12}s`
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    item.style.opacity = '1'
                    item.style.transform = 'translateY(0)'
                })
            })
        })
    }, [])

    return (
        <div className="not-found-root">
            {/* Ambient background shapes */}
            <div className="bg-shape bg-shape--1" />
            <div className="bg-shape bg-shape--2" />
            <div className="bg-shape bg-shape--3" />

            <div className="not-found-inner" ref={containerRef}>
                {/* Giant 404 */}
                <div className="reveal glitch-wrapper" aria-hidden="true">
                    <span className="glitch" data-text="404">404</span>
                </div>

                {/* Divider line */}
                <div className="reveal divider" />

                {/* Headline */}
                <h1 className="reveal not-found-title">
                    Page Not Found
                </h1>

                {/* Sub-copy */}
                <p className="reveal not-found-body">
                    The page you're looking for has moved, been removed,<br />
                    or perhaps never existed. Let's get you back on solid ground.
                </p>

                {/* CTAs */}
                <div className="reveal not-found-actions">
                    <Link to="/" className="btn-home">
                        Back to Home
                    </Link>
                    <Link to="/contact" className="btn-contact">
                        Contact Us
                    </Link>
                </div>

                {/* Quick nav */}
                <nav className="reveal not-found-nav">
                    <Link to="/about">About</Link>
                    <span className="dot" />
                    <Link to="/services">Services</Link>
                    <span className="dot" />
                    <Link to="/blog">Blog</Link>
                </nav>
            </div>

            <style>{`
                /* ── Root ── */
                .not-found-root {
                    position: relative;
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    background: #ffffff;
                    font-family: 'Georgia', serif;
                    padding: 2rem;
                }

                /* ── Ambient shapes ── */
                .bg-shape {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(80px);
                    opacity: 0.18;
                    pointer-events: none;
                }
                .bg-shape--1 {
                    width: 520px; height: 520px;
                    background: #c90404;
                    top: -120px; left: -160px;
                }
                .bg-shape--2 {
                    width: 380px; height: 380px;
                    background: #6e0f0f;
                    bottom: -80px; right: -100px;
                }
                .bg-shape--3 {
                    width: 260px; height: 260px;
                    background: #d10808;
                    top: 50%; left: 55%;
                    transform: translate(-50%, -50%);
                }

                /* ── Inner container ── */
                .not-found-inner {
                    position: relative;
                    z-index: 1;
                    text-align: center;
                    max-width: 640px;
                    width: 100%;
                }

                /* ── Glitch 404 ── */
                .glitch-wrapper {
                    line-height: 1;
                    margin-bottom: 0.5rem;
                }
                .glitch {
                    font-family: 'Georgia', serif;
                    font-size: clamp(7rem, 20vw, 14rem);
                    font-weight: 700;
                    color: #333131;
                    letter-spacing: -0.04em;
                    position: relative;
                    display: inline-block;
                }
                .glitch::before,
                .glitch::after {
                    content: attr(data-text);
                    position: absolute;
                    top: 0; left: 0;
                    width: 100%;
                }
                .glitch::before {
                    color: #b40a0a;
                    clip-path: polygon(0 0, 100% 0, 100% 35%, 0 35%);
                    animation: glitch-top 4s infinite linear;
                }
                .glitch::after {
                    color: #958f8f;
                    clip-path: polygon(0 65%, 100% 65%, 100% 100%, 0 100%);
                    animation: glitch-bot 4s infinite linear;
                }
                @keyframes glitch-top {
                    0%, 90%, 100% { transform: translate(0); }
                    92%           { transform: translate(-4px, -2px); }
                    94%           { transform: translate(4px, 2px); }
                    96%           { transform: translate(-2px, 0); }
                }
                @keyframes glitch-bot {
                    0%, 90%, 100% { transform: translate(0); }
                    93%           { transform: translate(4px, 2px); }
                    95%           { transform: translate(-4px, -1px); }
                    97%           { transform: translate(2px, 0); }
                }

                /* ── Divider ── */
                .divider {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, #3b82f6 40%, #93c5fd 60%, transparent);
                    margin: 1.25rem auto 2rem;
                    width: 60%;
                }

                /* ── Text ── */
                .not-found-title {
                    font-family: 'Georgia', serif;
                    font-size: clamp(1.5rem, 4vw, 2.25rem);
                    font-weight: 400;
                    color: #232324;
                    letter-spacing: 0.01em;
                    margin: 0 0 1.25rem;
                }
                .not-found-body {
                    font-family: Arial, sans-serif;
                    font-size: 1rem;
                    line-height: 1.75;
                    color: #f60505;
                    margin: 0 0 2.5rem;
                }

                /* ── Buttons ── */
                .not-found-actions {
                    display: flex;
                    gap: 1rem;
                    justify-content: center;
                    flex-wrap: wrap;
                    margin-bottom: 2.5rem;
                }
                .btn-home {
                    display: inline-block;
                    padding: 0.75rem 2rem;
                    background: #9c0909;
                    color: #fff;
                    text-decoration: none;
                    font-family: Arial, sans-serif;
                    font-size: 0.875rem;
                    font-weight: 600;
                    letter-spacing: 0.05em;
                    text-transform: uppercase;
                    border-radius: 3px;
                    transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
                    box-shadow: 0 0 0 0 #3b82f680;
                }
                .btn-home:hover {
                    background: #2e2f30;
                    transform: translateY(-2px);
                    box-shadow: 0 0 20px 4px #3b82f640;
                }
                .btn-contact {
                    display: inline-block;
                    padding: 0.75rem 2rem;
                    background: transparent;
                    color: #313233;
                    text-decoration: none;
                    font-family: Arial, sans-serif;
                    font-size: 0.875rem;
                    font-weight: 600;
                    letter-spacing: 0.05em;
                    text-transform: uppercase;
                    border-radius: 3px;
                    border: 1px solid #3e3e3f;
                    transition: border-color 0.2s, color 0.2s, transform 0.2s;
                }
                .btn-contact:hover {
                    border-color: #820808;
                    color: #bfdbfe;
                    transform: translateY(-2px);
                }

                /* ── Quick nav ── */
                .not-found-nav {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    justify-content: center;
                }
                .not-found-nav a {
                    font-family: Arial, sans-serif;
                    font-size: 0.8rem;
                    color: #475569;
                    text-decoration: none;
                    letter-spacing: 0.06em;
                    text-transform: uppercase;
                    transition: color 0.2s;
                }
                .not-found-nav a:hover { color: #3f4042; }
                .dot {
                    width: 3px; height: 3px;
                    background: #334155;
                    border-radius: 50%;
                    display: inline-block;
                }
            `}</style>
        </div>
    )
}

export default NotFound